import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { compactPageItems } from "../../components/ui/compactPagination";
import api from "../../api";
import { isComplaintTask, assigneeOptionsFor, assigneeOptionLabel, assignmentErrorMessage } from "../../utils/complaintAssignee";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Pagination,
  Spinner,
  Badge,
} from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import SearchableSelect from "../../components/SearchableSelect";
import useCountUp from "../../hooks/useCountUp";
import "./ProductUnitWisePage.css";
import "./TaskManagementPage.css";
import "./TechnicianDesignSystem.css";
import TechModalTitle from "./TechModalTitle";
import {
  getMonthKey,
  buildMonthOptions,
  monthKeyLabel,
  monthFileSuffix,
  sanitizeFileNamePart,
  csvQuote,
} from "../../utils/monthFilter";
import { isOwnUnitTaskCreator, ownUnitId, ownUnitName } from "../../utils/ownUnit";
import { taskDisplayTitle, taskDescriptionText } from "../../utils/taskDisplay";

// Simplified backdated Task Date -- local-date "YYYY-MM-DD", matching a
// native <input type="date">'s own value format. Never uses
// toISOString() (UTC-based -- can roll the calendar date back a day in a
// timezone east of UTC); built from local Date getters instead so "today"
// always means the browser's own local today.
function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// Closed/Cancelled removed from selectable options (filters and
// status-change dropdowns) -- they remain valid, readable statuses on
// existing historical tasks (see LEGACY_STATUS_LABELS below) but must no
// longer be chosen for a new task or a status change. Mirrors
// task-management-backend/utils/status.js's isLegacyNonSelectableTaskStatus().
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

// Display-only labels for legacy statuses that existing tasks may already
// have. Never used to populate a dropdown -- only to render a readable
// label/badge for a task already in one of these states.
const LEGACY_STATUS_LABELS = {
  closed: "Closed (Legacy)",
  cancelled: "Cancelled (Legacy)",
};

const SORT_OPTIONS = [
  { value: "created_at", label: "Created Date" },
  { value: "title", label: "Title" },
  { value: "status", label: "Status" },
  { value: "requested_for_name", label: "Staff" },
  { value: "assigned_to_name", label: "Assigned To" },
];

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Fixed "DD Mon YYYY, hh:mm AM/PM" format -- toLocaleString() used to render
// this differently per browser/OS locale (e.g. "9/3/2026, 11:20:37 AM" vs.
// "03/09/2026 11:20") so every row in the same table could look inconsistent.
// Built from local Date getters, same local-time approach as
// todayDateString() above.
const fmtDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";

  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTH_ABBR[d.getMonth()];
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
};

const fmtChatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const useDebouncedState = (initial = "", delay = 350) => {
  const [value, setValue] = useState(initial);
  const [debounced, setDebounced] = useState(initial);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return { value, setValue, debounced };
};

const SortIcon = ({ dir }) => {
  if (dir === "asc") return "▲";
  if (dir === "desc") return "▼";
  return "↕";
};

// Semantic chip system -- one reusable presentational component instead of
// repeating badge CSS per column. Each tone is a light-bg/dark-text/subtle
// border triple; callers only pick a tone name, never raw colors.
const CHIP_TONES = {
  blue: { bg: "#E8F1FF", text: "#1558B0", border: "#C7DCFF" },
  purple: { bg: "#F0EBFF", text: "#6241C7", border: "#DDD2FF" },
  indigo: { bg: "#EEF2FF", text: "#4338CA", border: "#D6DEFF" },
  cyan: { bg: "#E6FBFF", text: "#0E7490", border: "#BAE6FD" },
  orange: { bg: "#FFF1E6", text: "#C2410C", border: "#FBD5B5" },
  amber: { bg: "#FFF7E6", text: "#B45309", border: "#F8D99B" },
  green: { bg: "#E8F8EF", text: "#15803D", border: "#B7E8C9" },
  red: { bg: "#FFECEC", text: "#DC2626", border: "#F6C2C2" },
  gray: { bg: "#F3F4F6", text: "#4B5563", border: "#D1D5DB" },
};

const SemanticChip = ({ tone = "gray", icon, label }) => {
  const palette = CHIP_TONES[tone] || CHIP_TONES.gray;
  const text = label ?? "-";

  return (
    <span
      className="tt-chip"
      style={{
        background: palette.bg,
        color: palette.text,
        borderColor: palette.border,
      }}
      title={typeof text === "string" ? text : undefined}
    >
      {icon ? <i className={`fa-solid ${icon} tt-chip-icon`} aria-hidden="true"></i> : null}
      <span className="tt-chip-label">{text}</span>
    </span>
  );
};

// Status -> tone/icon is data-driven off the normalized status value (never
// row position). Casing is normalized so "Completed"/"COMPLETED"/"completed"
// all resolve the same way; the *displayed* label still comes from the
// existing getStatusLabel() below, unchanged.
const getStatusChipTone = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === "completed" || s === "closed") return "green";
  if (s === "in_progress") return "blue";
  if (s === "pending") return "amber";
  if (s === "cancelled" || s === "rejected") return "red";
  if (s === "on_hold" || s === "on hold") return "gray";
  return "gray";
};

const getStatusChipIcon = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === "completed" || s === "closed") return "fa-circle-check";
  if (s === "in_progress") return "fa-spinner";
  if (s === "pending") return "fa-clock";
  if (s === "cancelled" || s === "rejected") return "fa-circle-xmark";
  if (s === "on_hold" || s === "on hold") return "fa-pause";
  return "fa-circle-question";
};

// Category -> tone. An exact-match table covers the categories named in the
// design spec; a keyword fallback lets any *future* category (not just the
// ones visible today) still resolve to a sensible tone instead of always
// falling back to gray.
const CATEGORY_TONE_MAP = {
  "other it support": "blue",
  "software issue": "purple",
  "hardware issue": "orange",
  "network issue": "cyan",
  "system performance issue": "amber",
  "access / permission": "indigo",
  "access/permission": "indigo",
  "general issue": "gray",
};

const CATEGORY_TONE_KEYWORDS = [
  { test: /software|application|app\b/, tone: "purple" },
  { test: /hardware|device|equipment/, tone: "orange" },
  { test: /network|connectivity|wifi|internet/, tone: "cyan" },
  { test: /server|nas|infra/, tone: "cyan" },
  { test: /performance|slow|speed/, tone: "amber" },
  { test: /access|permission|security|login/, tone: "indigo" },
  { test: /support/, tone: "blue" },
];

const getCategoryChipTone = (category) => {
  const key = String(category || "").trim().toLowerCase();
  if (!key) return "gray";
  if (CATEGORY_TONE_MAP[key]) return CATEGORY_TONE_MAP[key];

  const match = CATEGORY_TONE_KEYWORDS.find((rule) => rule.test.test(key));
  return match ? match.tone : "gray";
};

const isSameMessages = (oldList = [], newList = []) => {
  if (oldList.length !== newList.length) return false;

  return oldList.every((oldMsg, index) => {
    const newMsg = newList[index];

    return (
      String(oldMsg.id) === String(newMsg.id) &&
      oldMsg.text === newMsg.text &&
      oldMsg.time === newMsg.time
    );
  });
};

export default function AdminTasksPage() {
  const { user } = useContext(AuthContext) || { user: null };
  const lockToOwnUnit = isOwnUnitTaskCreator(user);

  const token = localStorage.getItem("token") || "";
  const authConfig = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};

const getUserTaskUrl = () => {
  if (!user?.id) return null;

  // assigned tasks of logged-in user
  return `/tasks/technician/${user.id}`;
};
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const {
    value: search,
    setValue: setSearch,
    debounced: debouncedSearch,
  } = useDebouncedState("", 350);

  const [statusFilter, setStatusFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("ALL");

  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showCreate, setShowCreate] = useState(false);
  // No title: a Technician's task is identified by its Category/Subcategory.
  // Description is an optional free-text note, shown right after Staff / User.
  const [createForm, setCreateForm] = useState({
    requested_for: "",
    description: "",
    unit_id: "",
    category_id: "",
    sub_category_id: "",
    // Simplified backdated Task Date -- available to every user/role that
    // can already open this Add Task modal at all; no extra permission, no
    // feature flag. Defaults to today.
    task_date: todayDateString(),
  });
  // Create Ticket modal: fields the user has interacted with (blur/change) or
  // that a submit attempt has touched -- inline errors only render for these,
  // so a freshly opened modal never opens already covered in red.
  const [createTouched, setCreateTouched] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [subcategoryLoadFailed, setSubcategoryLoadFailed] = useState(false);
  // Synchronous double-submit guard -- `saving` state only flips on the next
  // render, so two fast clicks could otherwise both reach api.post.
  const submittingRef = useRef(false);
  const subcategoryRequestRef = useRef(0);

  const [showEdit, setShowEdit] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editAssignedTo, setEditAssignedTo] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editNote, setEditNote] = useState("");

  // Task the Task List row's own quick status dropdown is currently
  // saving, so only that one row shows a disabled/spinner state.
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const [showView, setShowView] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [remarkType, setRemarkType] = useState("general");

  // CHAT STATE
  const [chatOpen, setChatOpen] = useState(false);
  const [chatTaskId, setChatTaskId] = useState(null);
  const [chatTasks, setChatTasks] = useState([]);
  const [messages, setMessages] = useState({});
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const messagesEndRef = useRef(null);

useEffect(() => {
  if (!user?.id) {
    setLoading(false);
    return;
  }
  fetchInitialData();
}, [user?.id]);

  useEffect(() => {
    if (!createForm.unit_id) {
      setCategories([]);
      setSubcategories([]);
      setCreateForm((prev) => ({
        ...prev,
        category_id: "",
        sub_category_id: "",
      }));
      return;
    }

    fetchCategoriesByUnit(createForm.unit_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createForm.unit_id]);

  useEffect(() => {
    if (!createForm.category_id) {
      subcategoryRequestRef.current += 1; // drop any in-flight response
      setLoadingSubcategories(false);
      setSubcategoryLoadFailed(false);
      setSubcategories([]);
      setCreateForm((prev) => ({
        ...prev,
        sub_category_id: "",
      }));
      return;
    }

    fetchSubcategoriesByCategory(createForm.category_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createForm.category_id]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    unitFilter,
    assigneeFilter,
    staffFilter,
    monthFilter,
    pageSize,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatTaskId]);

 const fetchInitialData = async () => {
  try {
    setLoading(true);
    setError("");

    const taskUrl = getUserTaskUrl();

    if (!taskUrl) return;

    const [unitRes, userRes, eligibleRes, taskRes] = await Promise.all([
      api.get(`/units`, authConfig),
      api.get(`/users`, authConfig),
      api.get(`/tasks/eligible-users`, {
        ...authConfig,
        params: { purpose: "requested_for" },
      }),
      api.get(taskUrl, authConfig),
    ]);

    setUnits(Array.isArray(unitRes.data) ? unitRes.data : []);
    setUsers(Array.isArray(userRes.data) ? userRes.data : []);

    // The eligible-users endpoint has changed response shape across versions.
    // Normalize the supported shapes here so the Staff/User dropdown always
    // receives the actual array returned by the backend.
    // BUGFIX: this previously read from `userRes.data` (the unscoped
    // `/users` list -- every user in the system, technicians/admins
    // included) instead of `eligibleRes.data` (the scoped
    // `/tasks/eligible-users?purpose=requested_for` list). That made the
    // Add Task "Staff / User" dropdown show every account in the system
    // rather than only the staff eligible to have a task raised for them.
    const eligiblePayload = eligibleRes?.data;
    const eligibleUsers =
      Array.isArray(eligiblePayload)
        ? eligiblePayload
        : Array.isArray(eligiblePayload?.data)
          ? eligiblePayload.data
          : Array.isArray(eligiblePayload?.users)
            ? eligiblePayload.users
            : Array.isArray(eligiblePayload?.data?.users)
              ? eligiblePayload.data.users
              : Array.isArray(eligiblePayload?.results)
                ? eligiblePayload.results
                : Array.isArray(eligiblePayload?.data?.results)
                  ? eligiblePayload.data.results
                  : [];

    setStaffUsers(eligibleUsers);

    setTasks(Array.isArray(taskRes.data) ? taskRes.data : []);
  } catch (err) {
    console.error("Fetch Initial Task Data Error:", err);
    // Backend errors come in two shapes: legacy handlers return
    // `{ error: "..." }`, while ApiError/sendApiError-based handlers (e.g.
    // TAT/date validation) return `{ success:false, code, message: "..." }`.
    // Checking only `.error` silently swallowed every ApiError message and
    // showed this generic fallback instead -- check both fields.
    setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to load ticket data.");
  } finally {
    setLoading(false);
  }
};

const refreshTasks = useCallback(async () => {
  if (!user?.id) return;

  try {
    setLoading(true);
    setError("");

    const { data } = await api.get(
      `/tasks/technician/${user.id}`,
      authConfig
    );

    setTasks(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Fetch Tasks Error:", err);
    setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch assigned tickets.");
  } finally {
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user?.id]);
  // Technician-owned categories only (category_staff, or sub_category_staff
  // via one of the category's subcategories) — scoped server-side to the
  // authenticated technician, never by unit alone. `unitId` is kept as the
  // trigger for this fetch (still gates when it runs, see the effect above)
  // but is no longer sent to the API.
  const fetchCategoriesByUnit = async (unitId) => {
    try {
      const { data } = await api.get(
        `/categories/technician-owned`,
        authConfig
      );

      setCategories(Array.isArray(data) ? data : []);
      setSubcategories([]);
      setCreateForm((prev) => ({
        ...prev,
        category_id: "",
        sub_category_id: "",
      }));
    } catch (err) {
      console.error("Fetch Categories Error:", err);
      setCategories([]);
      setSubcategories([]);
    }
  };

  // Technician-owned subcategories only (sub_category_staff, scoped
  // server-side to the authenticated technician) -- mirrors
  // fetchCategoriesByUnit's use of /categories/technician-owned above, so
  // the Subcategory picker narrows the same way the Category picker does.
  const fetchSubcategoriesByCategory = async (categoryId) => {
    // A slow response for a previously picked Category must never overwrite
    // the list for the one picked since.
    const requestId = ++subcategoryRequestRef.current;
    setLoadingSubcategories(true);
    setSubcategoryLoadFailed(false);
    try {
      const { data } = await api.get(
        `/subcategories/technician-owned/${categoryId}`,
        authConfig
      );
      if (requestId !== subcategoryRequestRef.current) return;

      setSubcategories(Array.isArray(data) ? data : []);
      setCreateForm((prev) => ({
        ...prev,
        sub_category_id: "",
      }));
    } catch (err) {
      console.error("Fetch Subcategories Error:", err);
      if (requestId !== subcategoryRequestRef.current) return;
      setSubcategories([]);
      setSubcategoryLoadFailed(true);
    } finally {
      if (requestId === subcategoryRequestRef.current) setLoadingSubcategories(false);
    }
  };

  const getStatusLabel = (value) => {
    return (
      STATUS_OPTIONS.find((status) => status.value === value)?.label ||
      LEGACY_STATUS_LABELS[value] ||
      value ||
      "-"
    );
  };

  const getStatusClass = (status) => {
    if (status === "completed" || status === "closed") {
      return "unit-status unit-status-returned";
    }

    if (status === "cancelled") {
      return "unit-status unit-status-cancelled";
    }

    if (status === "in_progress") {
      return "unit-status unit-status-active";
    }

    return "unit-status";
  };

  const StatusBadge = ({ value }) => (
    <SemanticChip
      tone={getStatusChipTone(value)}
      icon={getStatusChipIcon(value)}
      label={getStatusLabel(value)}
    />
  );

  const normalizeRemarks = (taskData) => {
    const list = Array.isArray(taskData?.remarks) ? taskData.remarks : [];

    return list.map((remark) => {
      const createdBy = remark.created_by || {};
      const fromId =
        createdBy.id ||
        remark.created_by_id ||
        remark.created_by ||
        remark.user_id ||
        null;

      return {
        id: remark.id,
        text: remark.remark || "",
        type: remark.remark_type || "general",
        from: createdBy.name || remark.created_by_name || "Unknown",
        fromId,
        time: remark.created_at,
        mine: String(fromId) === String(user?.id),
      };
    });
  };

  const fetchChatRemarks = async (taskId) => {
    if (!taskId) return;

    try {
      const { data } = await api.get(
        `/tasks/${taskId}`,
        authConfig
      );

      const remarks = normalizeRemarks(data);

      setMessages((prev) => {
        const oldMessages = prev[taskId] || [];

        if (isSameMessages(oldMessages, remarks)) {
          return prev;
        }

        return {
          ...prev,
          [taskId]: remarks,
        };
      });

      setChatTasks((prev) =>
        prev.map((task) =>
          String(task.id) === String(taskId)
            ? {
                ...task,
                ...data,
                assigned_to_name:
                  data?.assigned_to?.name ||
                  data?.assigned_to_name ||
                  task.assigned_to_name,
                created_by_name:
                  data?.created_by?.name ||
                  data?.created_by_name ||
                  task.created_by_name,
                requested_for_name:
                  data?.requested_for?.name ||
                  data?.requested_for_name ||
                  task.requested_for_name,
                category_name:
                  data?.category?.name || data?.category_name || task.category_name,
                sub_category_name:
                  data?.sub_category?.name ||
                  data?.sub_category_name ||
                  task.sub_category_name,
                unit_name: data?.unit?.name || data?.unit_name || task.unit_name,
              }
            : task
        )
      );
    } catch (err) {
      console.error("Fetch Chat Remarks Error:", err);
      // keep the existing messages instead of clearing them
    }
  };

  useEffect(() => {
    if (!chatOpen || !chatTaskId) return;

    fetchChatRemarks(chatTaskId);

    const timer = setInterval(() => {
      fetchChatRemarks(chatTaskId);
    }, 2500);

    return () => clearInterval(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, chatTaskId, user?.id]);

  const openChat = (task) => {
    if (!task?.id) return;

    setChatTasks((prev) => {
      const exists = prev.some((item) => String(item.id) === String(task.id));
      return exists ? prev : [...prev, task];
    });

    setChatTaskId(task.id);
    setChatOpen(true);
    fetchChatRemarks(task.id);
  };

  const closeChat = () => {
    setChatOpen(false);
  };

  const switchChatTab = (taskId) => {
    setChatTaskId(taskId);
    fetchChatRemarks(taskId);
  };

  const closeChatTab = (taskId) => {
    const remaining = chatTasks.filter(
      (task) => String(task.id) !== String(taskId)
    );

    setChatTasks(remaining);

    if (String(chatTaskId) === String(taskId)) {
      const nextTask = remaining[0];
      setChatTaskId(nextTask?.id || null);

      if (nextTask?.id) {
        fetchChatRemarks(nextTask.id);
      }
    }

    if (remaining.length === 0) {
      setChatOpen(false);
      setChatTaskId(null);
    }
  };

  const sendChatMessage = async () => {
    const text = chatInput.trim();

    if (!text || !chatTaskId || chatSending) return;

    try {
      setChatSending(true);

      const tempMsg = {
        id: `temp-${Date.now()}`,
        text,
        from: user?.name || "You",
        fromId: user?.id,
        time: new Date().toISOString(),
        mine: true,
        type: "general",
      };

      setMessages((prev) => ({
        ...prev,
        [chatTaskId]: [...(prev[chatTaskId] || []), tempMsg],
      }));

      setChatInput("");

      await api.post(
        `/tasks/${chatTaskId}/remarks`,
        {
          remark: text,
          remark_type: "general",
          created_by: user?.id,
        },
        authConfig
      );

      await fetchChatRemarks(chatTaskId);
      await refreshTasks();
    } catch (err) {
      console.error("Send Chat Message Error:", err);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to send message.");

      await fetchChatRemarks(chatTaskId);
    } finally {
      setChatSending(false);
    }
  };

  // Month filter uses the task's creation timestamp (tasks.created_at) --
  // the same column this list is already ordered/sorted by server-side and
  // the only "when was this task created" column the API returns.
  const monthOptions = useMemo(
    () => buildMonthOptions(tasks, "created_at"),
    [tasks]
  );

  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase().trim();

    return tasks.filter((task) => {
      const okSearch =
        !q ||
        [
          task.title,
          task.description,
          task.unit_name,
          task.category_name,
          task.sub_category_name,
          task.status,
          task.assigned_to_name,
          task.requested_for_name,
          task.created_by_name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));

      const okStatus = !statusFilter || task.status === statusFilter;
      const okUnit = !unitFilter || String(task.unit_id) === String(unitFilter);
      const okAssignee =
        !assigneeFilter || String(task.assigned_to) === String(assigneeFilter);
      // "Staff" filter/column means "who the task/request is for" -- that is
      // `requested_for`, never `created_by` (which is now always the
      // authenticated creator, not the selected staff/user -- see the
      // task-creator-vs-requested-for fix).
      const okStaff =
        !staffFilter || String(task.requested_for) === String(staffFilter);
      const okMonth =
        monthFilter === "ALL" || getMonthKey(task.created_at) === monthFilter;

      return okSearch && okStatus && okUnit && okAssignee && okStaff && okMonth;
    });
  }, [tasks, debouncedSearch, statusFilter, unitFilter, assigneeFilter, staffFilter, monthFilter]);

  const sorted = useMemo(() => {
    const rows = [...filtered];

    rows.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const av = a?.[sortBy] ?? "";
      const bv = b?.[sortBy] ?? "";

      if (sortBy === "created_at" || sortBy === "updated_at") {
        return (new Date(av) - new Date(bv)) * dir;
      }

      return String(av).localeCompare(String(bv)) * dir;
    });

    return rows;
  }, [filtered, sortBy, sortDir]);

  // KPI cards are computed from the same filtered (month + other active
  // filters) cohort as the table, so the numbers stay consistent with what
  // is currently displayed instead of showing unrelated all-time totals.
  const counts = useMemo(() => {
    const result = {
      total: filtered.length,
      pending: 0,
      in_progress: 0,
      completed: 0,
      closed: 0,
      cancelled: 0,
      active: 0,
      unassigned: 0,
      completionRate: 0,
    };

    filtered.forEach((task) => {
      if (result[task.status] !== undefined) {
        result[task.status] += 1;
      }

      if (!task.assigned_to) {
        result.unassigned += 1;
      }
    });

    result.active = result.pending + result.in_progress;

    const finished = result.completed + result.closed;
    result.completionRate = result.total
      ? Math.round((finished / result.total) * 100)
      : 0;

    return result;
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const indexOfLast = page * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentRows = sorted.slice(indexOfFirst, indexOfLast);

  const serialNumber = (index) => (page - 1) * pageSize + index + 1;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setUnitFilter("");
    setAssigneeFilter("");
    setStaffFilter("");
    setMonthFilter("ALL");
    setSortBy("created_at");
    setSortDir("desc");
    setPage(1);
  };

  const onSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  // Exports every task matching the current filters (search/status/unit/
  // assignee/staff/month), not just the current page -- `sorted` already
  // holds the full filtered+sorted set the table paginates over. Only
  // display-safe joined fields are included (no raw internal user IDs,
  // workflow/TAT columns, or escalation notes).
  const exportCsv = () => {
    if (!sorted.length) {
      alert("No data available to export");
      return;
    }

    const headers = [
      "Ticket ID",
      "Ticket Title",
      "Description",
      "Staff / Raised By",
      "Unit",
      "Category",
      "Subcategory",
      "Assigned To",
      "Status",
      "Created Date",
      "Completed Date",
    ];

    const rows = sorted.map((task) => [
      task.id,
      taskDisplayTitle(task),
      task.description || "",
      task.requested_for_name || "",
      task.unit_name || "",
      task.category_name || "",
      task.sub_category_name || "",
      task.assigned_to_name || "",
      LEGACY_STATUS_LABELS[task.status] ||
        STATUS_OPTIONS.find((s) => s.value === task.status)?.label ||
        task.status ||
        "",
      task.created_at || "",
      task.completed_at || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((value) => csvQuote(value)).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const unitName = unitFilter
      ? units.find((u) => String(u.id) === String(unitFilter))?.name
      : null;
    const unitPart = unitName ? `-${sanitizeFileNamePart(unitName)}` : "";
    const monthPart = monthFileSuffix(monthFilter);

    link.href = url;
    link.download = `technician-tasks${unitPart}-${monthPart}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  // The technician's own unit is preselected and locked in Create Task.
  const technicianUnitId = user?.unit_id ? String(user.unit_id) : "";
  const technicianUnitLocked =
    !!technicianUnitId && units.some((u) => String(u.id) === technicianUnitId);

  const resetCreateForm = () => {
    setCreateForm({
      requested_for: "",
      description: "",
      // Own unit for Staff/Technician/Manager (never units[0]); others pick.
      unit_id: lockToOwnUnit ? ownUnitId(user) : "",
      category_id: "",
      sub_category_id: "",
      task_date: todayDateString(),
    });
    setCreateTouched({});
    setSubcategoryLoadFailed(false);
    setCategories([]);
    setSubcategories([]);
  };

  const openCreateModal = () => {
    resetCreateForm();
    setShowCreate(true);
  };

  // Estimated TAT -- read-only display of the Subcategory's own configured
  // tat_value/tat_unit (services/subcategoryTatService.js is the single
  // source of truth). Never editable here and never sent in the payload --
  // the backend independently resolves TAT from sub_category_id alone.
  const selectedSubcategory = subcategories.find(
    (s) => String(s.id) === String(createForm.sub_category_id)
  );
  const subcategoryHasTat = !!(
    selectedSubcategory &&
    selectedSubcategory.tat_value != null &&
    selectedSubcategory.tat_unit
  );

  // Required-field rules for the Create Ticket modal (unchanged: the same
  // five values the Create button and onCreate already required; the future-
  // date rule just surfaces the existing `max` attribute / backend check).
  const createErrors = {
    requested_for: createForm.requested_for ? "" : "Select the staff/user this ticket is for.",
    unit_id: createForm.unit_id ? "" : "Unit is required.",
    category_id: createForm.category_id ? "" : "Select a category.",
    sub_category_id: createForm.sub_category_id ? "" : "Select a subcategory.",
    task_date: !createForm.task_date
      ? "Select the ticket date."
      : createForm.task_date > todayDateString()
        ? "Ticket date cannot be in the future."
        : "",
  };
  const createIsValid = Object.values(createErrors).every((message) => !message);
  const createError = (field) => (createTouched[field] ? createErrors[field] : "");
  const touchCreateField = (field) =>
    setCreateTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

  const handleCloseCreate = () => {
    // Never dismiss (Esc / backdrop / X) while the request is in flight.
    if (!saving) setShowCreate(false);
  };

  const onCreate = async () => {
    if (submittingRef.current) return;

    if (!createIsValid) {
      setCreateTouched({
        requested_for: true,
        unit_id: true,
        category_id: true,
        sub_category_id: true,
        task_date: true,
      });
      return;
    }

    submittingRef.current = true;
    try {
      setSaving(true);

      await api.post(
        `/tasks`,
        {
          // No title -- the backend stores NULL for Technician tasks.
          // Description is optional and only sent when the user typed one.
          ...(createForm.description.trim()
            ? { description: createForm.description.trim() }
            : {}),
          // The selected staff/user this task is being created on behalf of.
          // The backend always derives `created_by` (the actual creator/
          // audit user) from the authenticated session -- it is never taken
          // from this payload, so there is nothing here to spoof it with.
          requested_for: Number(createForm.requested_for),
          unit_id: Number(createForm.unit_id),
          category_id: Number(createForm.category_id),
          sub_category_id: Number(createForm.sub_category_id),
          assigned_to: user?.id ? Number(user.id) : undefined, // task is assigned to the logged-in technician
          // Simplified backdated Task Date -- backend independently
          // re-validates (never trusts this alone), rejecting a future date
          // regardless of what a crafted request sends.
          task_date: createForm.task_date,
          // No tat_value/tat_unit is ever sent -- TAT is resolved purely
          // server-side from category_id/sub_category_id.
        },
        authConfig
      );

      setShowCreate(false);
      resetCreateForm();
      await refreshTasks();

      alert("Ticket created successfully");
    } catch (err) {
      console.error("Create Task Error:", err);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to create ticket.");
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  const openEdit = (task) => {
    setEditTask(task);
    setEditAssignedTo(task.assigned_to || "");
    setEditStatus(task.status || "");
    setEditNote("");
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editTask) return;

    try {
      setSaving(true);

      if (editAssignedTo) {
        await api.put(
          `/tasks/assign/${editTask.id}`,
          {
            assigned_to: Number(editAssignedTo),
          },
          authConfig
        );
      }

      if (editStatus) {
        await api.put(
          `/tasks/${editTask.id}/status`,
          {
            status: editStatus,
            note: editNote || undefined,
          },
          authConfig
        );
      }

      setShowEdit(false);
      setEditTask(null);
      await refreshTasks();

      alert("Ticket updated successfully");
    } catch (err) {
      console.error("Save Task Error:", err);
      alert(assignmentErrorMessage(err, "Failed to save changes."));
    } finally {
      setSaving(false);
    }
  };

  // Quick status change directly from the Task List row -- same
  // PUT /tasks/:id/status endpoint the Edit modal already uses, just
  // without opening a modal first for the common case of "just move this
  // one task along."
  const updateTaskStatusInline = async (taskId, newStatus) => {
    if (!newStatus) return;

    try {
      setStatusUpdatingId(taskId);

      await api.put(
        `/tasks/${taskId}/status`,
        { status: newStatus },
        authConfig
      );

      await refreshTasks();
    } catch (err) {
      console.error("Inline Status Update Error:", err);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to update status.");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openView = async (id) => {
    try {
      const { data } = await api.get(
        `/tasks/${id}`,
        authConfig
      );

      setViewTask(data);
      setRemarkText("");
      setRemarkType("general");
      setShowView(true);
    } catch (err) {
      console.error("Open Task Error:", err);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to open ticket.");
    }
  };

  const addRemark = async () => {
    if (!viewTask || !remarkText.trim()) return;

    try {
      setSaving(true);

      await api.post(
        `/tasks/${viewTask.id}/remarks`,
        {
          remark: remarkText.trim(),
          remark_type: remarkType || "general",
          created_by: user?.id,
        },
        authConfig
      );

      const { data } = await api.get(
        `/tasks/${viewTask.id}`,
        authConfig
      );

      setViewTask(data);
      setRemarkText("");
      await refreshTasks();
    } catch (err) {
      console.error("Add Remark Error:", err);
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to add remark.");
    } finally {
      setSaving(false);
    }
  };

  const StatCard = ({ label, value, meta, icon, tone = "info", className = "" }) => {
    const displayValue = useCountUp(value);
    return (
    <div className={`unit-stat-card puws-stat task-kpi-card task-kpi-tone-${tone} ${className} tech-kpi tech-tone-${tone}`}>
      {icon ? (
        <div className="task-kpi-icon tech-kpi-icon" aria-hidden="true">
          <i className={`fa-solid ${icon}`}></i>
        </div>
      ) : null}

      <div className="task-kpi-body tech-kpi-body">
        <div className="puws-stat-lbl tech-kpi-label">{label}</div>
        <div className="puws-stat-val tech-kpi-value">{displayValue}</div>
        {meta ? <small className="task-kpi-meta tech-kpi-meta">{meta}</small> : null}
      </div>
    </div>
    );
  };

  const currentChatTask = chatTasks.find(
    (task) => String(task.id) === String(chatTaskId)
  );

  const currentMsgs = messages[chatTaskId] || [];

  return (
    <div className="unit-page techtasks-theme tech-ds">
      <div className="unit-header tech-page-header">
        <div>
          <div className="unit-page-label tech-section-label">Ticket Management</div>
          <h2 className="tech-page-title">Ticket Dashboard</h2>
          <p className="tech-page-subtitle">
            Create tickets, assign technicians, track status updates, remarks and
            ticket progress.
          </p>
        </div>

        <div className="unit-header-actions tech-header-actions">
          <Button className="unit-btn unit-btn-light puws-btn puws-btn-ghost" onClick={fetchInitialData}>
            <i className="fa-solid fa-arrow-rotate-right" aria-hidden="true"></i>
            Refresh
          </Button>

          <Button className="unit-btn unit-btn-light puws-btn puws-btn-ghost" onClick={exportCsv}>
            <i className="fa-solid fa-file-export" aria-hidden="true"></i>
            Export CSV
          </Button>

          <Button className="unit-btn unit-btn-primary puws-btn puws-btn-primary" onClick={openCreateModal}>
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            Add Ticket
          </Button>
        </div>
      </div>

      <div className="task-stats-grid puws-stats tech-kpis tech-kpis--4">
        <StatCard
          label="Total Tickets"
          value={counts.total}
          meta={`${counts.active} active tickets`}
          className="task-kpi-total s1"
          icon="fa-list-check"
          tone="info"
        />

        <StatCard
          label="Pending"
          value={counts.pending}
          meta={`${counts.unassigned} unassigned`}
          className="task-kpi-pending s4"
          icon="fa-clock"
          tone="warning"
        />

        <StatCard
          label="In Progress"
          value={counts.in_progress}
          meta="Being worked on"
          className="task-kpi-progress s2"
          icon="fa-spinner"
          tone="purple"
        />

        <StatCard
          label="Completed"
          value={counts.completed}
          meta={`${counts.completionRate}% done`}
          className="task-kpi-completed s3"
          icon="fa-circle-check"
          tone="success"
        />
      </div>

      <div className="unit-card tech-card">
        <div className="unit-toolbar tech-toolbar">
          <div className="tech-toolbar-title">
            <h5>Ticket List</h5>
            <p>Search, filter and track ticket status, assignees and progress.</p>
          </div>

          <div className="unit-toolbar-actions tech-filters tech-filters--dense">
            <InputGroup className="unit-search puws-search-wrap tech-search">
              <InputGroup.Text>
                <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              </InputGroup.Text>

              <Form.Control
                className="puws-search"
                placeholder="Search title, category, status, assignee..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <Button className="unit-clear-btn" onClick={() => setSearch("")}>
                  <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                  Clear
                </Button>
              )}
            </InputGroup>

            <Form.Select
              className="unit-page-size puws-select tech-select"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              aria-label="Filter by month"
              title="Filter by created month"
            >
              <option value="ALL">All Months</option>
              {monthOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select" aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select" aria-label="Filter by unit"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
            >
              <option value="">All Units</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select tech-select--wide" aria-label="Filter by assignee"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
            >
              <option value="">All Assignees</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select" aria-label="Filter by staff"
              value={staffFilter}
              onChange={(e) => setStaffFilter(e.target.value)}
            >
              <option value="">All Staff</option>
              {staffUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select tech-select--sort" aria-label="Sort tickets"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((sort) => (
                <option key={sort.value} value={sort.value}>
                  Sort: {sort.label}
                </option>
              ))}
            </Form.Select>

            <Form.Select
              className="unit-page-size puws-select tech-select tech-select--rows" aria-label="Rows per page"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={5}>5 rows</option>
              <option value={10}>10 rows</option>
              <option value={20}>20 rows</option>
              <option value={50}>50 rows</option>
            </Form.Select>

            {(search || statusFilter || unitFilter || assigneeFilter || staffFilter || monthFilter !== "ALL") && (
              <Button
                className="unit-btn unit-btn-light puws-btn puws-btn-ghost tech-reset-btn"
                onClick={clearFilters}
                aria-label="Reset filters"
                title="Reset filters"
              >
                <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
                <span className="tech-reset-label">Reset</span>
              </Button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger mx-3">{error}</div>}

        <div className="puws-table-wrap tech-table-wrap">
          {loading ? (
            <div className="puws-loader">
              <Spinner animation="border" />
              <p>Loading tickets...</p>
            </div>
          ) : (
            <table className="puws-table tech-table tech-table--tasks">
              <colgroup>
                <col className="tt-col-sno" />
                <col className="tt-col-task" />
                <col className="tt-col-staff" />
                <col className="tt-col-unit" />
                <col className="tt-col-category" />
                <col className="tt-col-assigned" />
                <col className="tt-col-status" />
                <col className="tt-col-created" />
                <col className="tt-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="tech-th-center">S.No</th>

                  <th
                    role="button"
                    onClick={() => onSort("title")}
                    className="user-select-none"
                  >
                    Ticket{" "}
                    <small>
                      <SortIcon dir={sortBy === "title" ? sortDir : null} />
                    </small>
                  </th>

                  <th
                    role="button"
                    onClick={() => onSort("requested_for_name")}
                    className="user-select-none"
                  >
                    Staff{" "}
                    <small>
                      <SortIcon
                        dir={sortBy === "requested_for_name" ? sortDir : null}
                      />
                    </small>
                  </th>

                  <th>Unit</th>
                  <th>Category</th>

                  <th
                    role="button"
                    onClick={() => onSort("assigned_to_name")}
                    className="user-select-none"
                  >
                    Assigned{" "}
                    <small>
                      <SortIcon
                        dir={sortBy === "assigned_to_name" ? sortDir : null}
                      />
                    </small>
                  </th>

                  <th
                    role="button"
                    onClick={() => onSort("status")}
                    className="user-select-none"
                  >
                    Status{" "}
                    <small>
                      <SortIcon dir={sortBy === "status" ? sortDir : null} />
                    </small>
                  </th>

                  <th
                    role="button"
                    onClick={() => onSort("created_at")}
                    className="user-select-none"
                  >
                    Created{" "}
                    <small>
                      <SortIcon
                        dir={sortBy === "created_at" ? sortDir : null}
                      />
                    </small>
                  </th>

                  <th className="tech-th-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentRows.length > 0 ? (
                  currentRows.map((task, index) => (
                    <tr key={task.id}>
                      <td className="tech-td-center">
                        <span className="unit-id tech-sno">{serialNumber(index)}</span>
                      </td>

                      <td>
                        <div className="unit-name-box tech-primary-cell">
                          <div className="unit-letter tech-avatar">
                            {taskDisplayTitle(task).charAt(0).toUpperCase() || "T"}
                          </div>

                          <div className="unit-name-text tech-primary-text">
                            <strong className="tech-primary" title={taskDisplayTitle(task) || undefined}>
                              {taskDisplayTitle(task) || "-"}
                            </strong>
                            <small className="tech-secondary" title={taskDescriptionText(task) || undefined}>
                              {taskDescriptionText(task) || "—"}
                            </small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="admin-task-staff-cell">
                          <strong className="tech-staff">{task.requested_for_name || "-"}</strong>
                        </div>
                      </td>

                      <td>
                        <SemanticChip tone="blue" icon="fa-building" label={task.unit_name || "-"} />
                      </td>

                      <td>
                        <div className="tt-category-cell">
                          <SemanticChip
                            tone={getCategoryChipTone(task.category_name)}
                            label={task.category_name || "-"}
                          />
                          {task.sub_category_name ? (
                            <small className="d-block text-muted tt-subcategory">
                              {task.sub_category_name}
                            </small>
                          ) : null}
                        </div>
                      </td>

                      <td>
                        <SemanticChip
                          tone={task.assigned_to_name ? "purple" : "gray"}
                          icon="fa-user"
                          label={task.assigned_to_name || "Not assigned"}
                        />
                      </td>

                      <td>
                        {STATUS_OPTIONS.some((opt) => opt.value === task.status) ? (
                          <Form.Select
                            size="sm"
                            className="tt-status-select"
                            style={{
                              background: CHIP_TONES[getStatusChipTone(task.status)].bg,
                              color: CHIP_TONES[getStatusChipTone(task.status)].text,
                              borderColor: CHIP_TONES[getStatusChipTone(task.status)].border,
                            }}
                            value={task.status}
                            disabled={statusUpdatingId === task.id}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              updateTaskStatusInline(task.id, e.target.value)
                            }
                            aria-label={`Change status for ticket ${taskDisplayTitle(task)}`}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          // Legacy closed/cancelled tasks stay read-only chips --
                          // these statuses are no longer selectable for a status
                          // change (see STATUS_OPTIONS above), only readable on
                          // tasks that already have them.
                          <StatusBadge value={task.status} />
                        )}
                      </td>

                      <td>
                        <span className="tech-date">{fmtDateTime(task.created_at)}</span>
                      </td>

                      <td>
                        <div className="unit-actions task-action-line tech-actions">
                          <Button
                            className="unit-icon-btn unit-view tech-act tech-act--view"
                            title="View"
                            aria-label={`View ticket ${taskDisplayTitle(task)}`}
                            onClick={() => openView(task.id)}
                          >
                            <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-edit tech-act tech-act--edit"
                            title="Edit"
                            aria-label={`Edit ticket ${taskDisplayTitle(task)}`}
                            onClick={() => openEdit(task)}
                          >
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>

                          <Button
                            className="unit-icon-btn unit-chat tech-act tech-act--comment"
                            title="Chat"
                            aria-label={`Open chat for ticket ${taskDisplayTitle(task)}`}
                            onClick={() => openChat(task)}
                          >
                            <i className="fa-solid fa-comment-dots" aria-hidden="true"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i>
                        </div>
                        <h5>No tickets found</h5>
                        <p>
                          {monthFilter !== "ALL"
                            ? `No records found for ${monthKeyLabel(monthFilter)}.`
                            : "Try changing filters or create a new ticket."}
                        </p>

                        <Button
                          className="unit-btn unit-btn-primary"
                          onClick={openCreateModal}
                        >
                          <i className="fa-solid fa-plus" aria-hidden="true"></i>
                          Add Ticket
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="unit-footer tech-footer">
          <div className="unit-count-text">
            Showing <strong>{sorted.length === 0 ? 0 : indexOfFirst + 1}</strong>{" "}
            to <strong>{Math.min(indexOfLast, sorted.length)}</strong> of{" "}
            <strong>{sorted.length}</strong> entries
          </div>

          {totalPages > 1 && (
            <Pagination className="unit-pagination tech-pagination">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              />

              {compactPageItems([...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === page}
                  onClick={() => setPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              )), page)}

              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              />
            </Pagination>
          )}
        </div>
      </div>

      {/* CHAT DRAWER */}
      {chatOpen && (
        <div
          className="task-chat-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeChat();
          }}
        >
          <div className="task-chat-panel">
            <div className="task-chat-tabs">
              {chatTasks.map((task) => (
                <button
                  key={task.id}
                  className={`task-chat-tab ${
                    String(task.id) === String(chatTaskId) ? "active" : ""
                  }`}
                  onClick={() => switchChatTab(task.id)}
                >
                  <span className="task-chat-tab-title">
                    💬 #{task.id} ·{" "}
                    {taskDisplayTitle(task).length > 16
                      ? `${taskDisplayTitle(task).slice(0, 16)}...`
                      : taskDisplayTitle(task)}
                  </span>

                  <span
                    className="task-chat-tab-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeChatTab(task.id);
                    }}
                  >
                    ×
                  </span>
                </button>
              ))}
            </div>

            <div className="task-chat-header">
              <div className="task-chat-title-wrap">
                <h5>{taskDisplayTitle(currentChatTask) || "Ticket Chat"}</h5>

                <p>
                  {currentChatTask?.assigned_to_name
                    ? `Technician: ${currentChatTask.assigned_to_name}`
                    : "Technician not assigned"}

                  {currentChatTask?.status ? (
                    <span className={getStatusClass(currentChatTask.status)}>
                      {getStatusLabel(currentChatTask.status)}
                    </span>
                  ) : null}
                </p>
              </div>

              <button className="task-chat-main-close" onClick={closeChat}>
                ×
              </button>
            </div>

            <div className="task-chat-meta">
              <div>
                <span>Staff</span>
                <strong>{currentChatTask?.requested_for_name || "-"}</strong>
              </div>

              <div>
                <span>Unit</span>
                <strong>{currentChatTask?.unit_name || "-"}</strong>
              </div>

              <div>
                <span>Category</span>
                <strong>{currentChatTask?.category_name || "-"}</strong>
              </div>

              <div>
                <span>Created</span>
                <strong>{fmtDateTime(currentChatTask?.created_at)}</strong>
              </div>
            </div>

            <div className="task-chat-messages">
              {currentMsgs.length === 0 ? (
                <div className="task-chat-empty">
                  <div>💬</div>
                  <h6>No messages yet</h6>
                  <p>Start conversation on this ticket.</p>
                </div>
              ) : (
                currentMsgs.map((msg, index) => (
                  <div
                    key={msg.id || index}
                    className={`task-chat-msg ${msg.mine ? "mine" : "theirs"}`}
                  >
                    {!msg.mine && (
                      <div className="task-chat-sender">{msg.from}</div>
                    )}

                    <div className="task-chat-bubble">{msg.text}</div>

                    <div className="task-chat-time">{fmtChatTime(msg.time)}</div>
                  </div>
                ))
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="task-chat-input-area">
              <textarea
                className="task-chat-input"
                placeholder="Type message and press Enter..."
                rows={1}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
              />

              <Button
                className="task-chat-send"
                disabled={!chatInput.trim() || chatSending}
                onClick={sendChatMessage}
              >
                {chatSending ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "➤"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE MODAL
          Layout: header / scrollable body / footer (styles: `.tech-create-modal`
          in TaskManagementPage.css). `scrollable` is intentionally NOT used --
          react-bootstrap's version gives the dialog a fixed full-height box;
          here the content is capped with max-height and only the body scrolls.
          Validation: fields show inline errors only after blur/change or a
          submit attempt (createTouched), and Create stays disabled until the
          same required values onCreate always demanded are present. */}
      <Modal
        show={showCreate}
        onHide={handleCloseCreate}
        onEntered={() => document.getElementById("tcm-staff")?.focus()}
        centered
        aria-labelledby="tcm-title"
        className="unit-modal techtasks-modal tech-modal tech-create-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle id="tcm-title" icon="fa-square-plus">Create Ticket</TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="tcm-grid">
            <div className="tcm-field tcm-field--full">
              <Form.Label htmlFor="tcm-staff" className="tcm-label">
                Staff / User <span className="text-danger" aria-hidden="true">*</span>
                <span className="tcm-sr-only"> (required)</span>
              </Form.Label>

              <SearchableSelect
                inputId="tcm-staff"
                options={staffUsers.map((staff) => ({
                  value: String(staff.id),
                  label: `${staff.name}${staff.email ? ` - ${staff.email}` : ""}`,
                }))}
                value={createForm.requested_for}
                onChange={(v) => {
                  setCreateForm((prev) => ({
                    ...prev,
                    requested_for: v,
                  }));
                  touchCreateField("requested_for");
                }}
                onBlur={() => touchCreateField("requested_for")}
                isInvalid={!!createError("requested_for")}
                errorId="tcm-err-staff"
                placeholder="Select staff/user whose ticket this is"
              />

              {createError("requested_for") ? (
                <div id="tcm-err-staff" className="tcm-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  {createError("requested_for")}
                </div>
              ) : staffUsers.length === 0 ? (
                <div className="tcm-help tcm-help--error">
                  No staff/users are available. Your account may not have permission to view any staff/users for task creation — please contact an administrator.
                </div>
              ) : (
                <div className="tcm-help">
                  Choose which staff/user this task is being created on behalf of.
                </div>
              )}
            </div>

            <div className="tcm-field tcm-field--full">
              <Form.Label htmlFor="tcm-description" className="tcm-label">
                Description
              </Form.Label>

              <Form.Control
                id="tcm-description"
                as="textarea"
                rows={3}
                className="tcm-textarea"
                placeholder="Describe the issue (optional)"
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>

            <div className="tcm-field">
              <Form.Label htmlFor="tcm-unit" className="tcm-label">
                Unit <span className="text-danger" aria-hidden="true">*</span>
                <span className="tcm-sr-only"> (required)</span>
              </Form.Label>

              {lockToOwnUnit ? (
                // Read-only: the backend rejects any other unit (403).
                // Styled as a quiet fixed value (lock icon, normal-contrast
                // text) rather than the browser's greyed-out disabled look.
                <div className="tcm-readonly">
                  <Form.Control
                    id="tcm-unit"
                    value={ownUnitName(user, units) || (ownUnitId(user) ? "" : "No unit assigned to your account")}
                    readOnly
                    disabled
                    aria-label="Unit"
                    title="Your unit is fixed and cannot be changed"
                  />
                  <i className="fa-solid fa-lock tcm-readonly-ico" aria-hidden="true"></i>
                </div>
              ) : (
                <>
                  <SearchableSelect
                    inputId="tcm-unit"
                    options={units.map((unit) => ({
                      value: String(unit.id),
                      label: unit.name,
                    }))}
                    value={createForm.unit_id}
                    onChange={(v) => {
                      setCreateForm((prev) => ({
                        ...prev,
                        unit_id: v,
                      }));
                      setCreateTouched((prev) => ({
                        ...prev,
                        unit_id: true,
                        category_id: false,
                        sub_category_id: false,
                      }));
                    }}
                    onBlur={() => touchCreateField("unit_id")}
                    isInvalid={!!createError("unit_id")}
                    errorId="tcm-err-unit"
                    placeholder="Select Unit"
                  />
                  {createError("unit_id") ? (
                    <div id="tcm-err-unit" className="tcm-error" role="alert">
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                      {createError("unit_id")}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="tcm-field">
              <Form.Label htmlFor="tcm-category" className="tcm-label">
                Category <span className="text-danger" aria-hidden="true">*</span>
                <span className="tcm-sr-only"> (required)</span>
              </Form.Label>

              <SearchableSelect
                inputId="tcm-category"
                options={categories.map((category) => ({
                  value: String(category.id),
                  label: category.name,
                }))}
                value={createForm.category_id}
                isDisabled={!createForm.unit_id}
                onChange={(v) => {
                  // A new parent Category always invalidates the old
                  // Subcategory (the Category effect re-fetches the list).
                  if (v && v !== String(createForm.category_id)) setLoadingSubcategories(true);
                  setCreateForm((prev) => ({
                    ...prev,
                    category_id: v,
                    sub_category_id: "",
                  }));
                  setCreateTouched((prev) => ({
                    ...prev,
                    category_id: true,
                    sub_category_id: false,
                  }));
                }}
                onBlur={() => touchCreateField("category_id")}
                isInvalid={!!createError("category_id")}
                errorId="tcm-err-category"
                placeholder="Select Category"
              />

              {createError("category_id") ? (
                <div id="tcm-err-category" className="tcm-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  {createError("category_id")}
                </div>
              ) : null}
            </div>

            <div className="tcm-field tcm-field--full">
              <Form.Label htmlFor="tcm-subcategory" className="tcm-label">
                Subcategory <span className="text-danger" aria-hidden="true">*</span>
                <span className="tcm-sr-only"> (required)</span>
              </Form.Label>

              <SearchableSelect
                inputId="tcm-subcategory"
                options={subcategories.map((subcategory) => ({
                  value: String(subcategory.id),
                  label: subcategory.name,
                }))}
                value={createForm.sub_category_id}
                isDisabled={!createForm.category_id}
                isLoading={loadingSubcategories}
                onChange={(v) => {
                  setCreateForm((prev) => ({
                    ...prev,
                    sub_category_id: v,
                  }));
                  touchCreateField("sub_category_id");
                }}
                onBlur={() => touchCreateField("sub_category_id")}
                isInvalid={!!createError("sub_category_id")}
                errorId="tcm-err-subcategory"
                placeholder={
                  !createForm.category_id
                    ? "Select a category first"
                    : loadingSubcategories
                      ? "Loading..."
                      : "Select Subcategory"
                }
              />

              {createError("sub_category_id") ? (
                <div id="tcm-err-subcategory" className="tcm-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  {createError("sub_category_id")}
                </div>
              ) : createForm.category_id && !loadingSubcategories && subcategoryLoadFailed ? (
                <div className="tcm-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  Couldn't load subcategories. Re-select the category to try again.
                </div>
              ) : createForm.category_id && !loadingSubcategories && subcategories.length === 0 ? (
                <div className="tcm-help">
                  No subcategories are available for this category.
                </div>
              ) : null}
            </div>

            {createForm.sub_category_id && (
              <div className="tcm-field tcm-field--full">
                <Form.Label htmlFor="tcm-tat" className="tcm-label">Estimated TAT</Form.Label>
                {subcategoryHasTat ? (
                  <Form.Control
                    id="tcm-tat"
                    className="tcm-tat"
                    readOnly
                    value={`${selectedSubcategory.tat_value} ${selectedSubcategory.tat_unit}`}
                  />
                ) : (
                  <div className="tcm-help">
                    No Turnaround Time is configured for this subcategory yet — it will be created without a due date.
                  </div>
                )}
              </div>
            )}

            {/* Simplified backdated Task Date -- available to every
                user/role that can already open this Add Task modal at all;
                no permission, no feature flag, no role condition. `max`
                blocks a future pick in the UI as a courtesy only -- the
                backend independently re-validates and rejects a future
                date regardless of what a crafted request sends. The value
                stays the native "YYYY-MM-DD" string (displayed in the
                browser's own locale format, e.g. DD/MM/YYYY). */}
            <div className="tcm-field tcm-field--full">
              <label htmlFor="tcm-date" className="form-label tcm-label">
                Ticket Date <span className="text-danger" aria-hidden="true">*</span>
                <span className="tcm-sr-only"> (required)</span>
              </label>

              <input
                id="tcm-date"
                type="date"
                name="task_date"
                value={createForm.task_date}
                max={todayDateString()}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    task_date: e.target.value,
                  }))
                }
                onBlur={() => touchCreateField("task_date")}
                onClick={(e) => {
                  // Make the whole field open the picker, not just the icon.
                  try {
                    e.currentTarget.showPicker?.();
                  } catch (_) {
                    /* showPicker needs a user gesture / isn't supported -- the native icon still works */
                  }
                }}
                required
                aria-required="true"
                aria-invalid={createError("task_date") ? true : undefined}
                aria-describedby={createError("task_date") ? "tcm-err-date" : undefined}
                className={`form-control tcm-date${createError("task_date") ? " tcm-invalid" : ""}`}
              />

              {createError("task_date") ? (
                <div id="tcm-err-date" className="tcm-error" role="alert">
                  <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                  {createError("task_date")}
                </div>
              ) : null}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <p className="tcm-required-note">
            Fields marked with <span className="text-danger">*</span> are required.
          </p>

          <div className="tcm-actions">
            <Button
              className="unit-btn unit-btn-light"
              onClick={handleCloseCreate}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              className="unit-btn unit-btn-primary"
              onClick={onCreate}
              disabled={saving || !createIsValid}
              aria-busy={saving}
            >
              {saving ? (
                <Spinner animation="border" size="sm" aria-hidden="true" />
              ) : (
                <i className="fa-solid fa-check" aria-hidden="true"></i>
              )}
              {saving ? "Creating..." : "Create Ticket"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* EDIT MODAL */}
      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        centered
        className="unit-modal techtasks-modal tech-modal"
      >
        <Modal.Header closeButton>
          <TechModalTitle icon="fa-pen-to-square">Edit Ticket</TechModalTitle>
        </Modal.Header>

        <Modal.Body>
          <div className="alert alert-light border mb-3">
            <strong>{taskDisplayTitle(editTask) || "-"}</strong>
            {editTask?.description ? (
              <div className="text-muted small mt-1">{editTask.description}</div>
            ) : null}
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                {/* GET /tasks/technician/:id returns only IT Support /
                    legacy complaints, so this is Technicians + the current
                    owner (utils/complaintAssignee.js). */}
                <Form.Label htmlFor="tech-edit-assignee">{isComplaintTask(editTask) ? "Assign Technician" : "Assign To"}</Form.Label>

                <Form.Select
                  id="tech-edit-assignee"
                  value={editAssignedTo}
                  onChange={(e) => setEditAssignedTo(e.target.value)}
                >
                  <option value="">{isComplaintTask(editTask) ? "Select Technician" : "Select User"}</option>
                  {assigneeOptionsFor(editTask, users).map((u) => (
                    <option key={u.id} value={u.id}>
                      {assigneeOptionLabel(u)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Status</Form.Label>

                <Form.Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <option value="">Select Status</option>
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label>Note</Form.Label>

                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Optional note..."
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light"
            onClick={() => setShowEdit(false)}
            disabled={saving}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            Cancel
          </Button>

          <Button
            className="unit-btn unit-btn-primary"
            onClick={saveEdit}
            disabled={saving}
          >
            <i
              className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"}`}
              aria-hidden="true"
            ></i>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW MODAL */}
      <Modal
        show={showView}
        onHide={() => setShowView(false)}
        size="lg"
        centered
        className="unit-modal techtasks-modal tdv-modal"
        backdropClassName="tdv-backdrop"
      >
        <Modal.Header closeButton>
          <div className="tdv-header-left">
            <div className="tdv-header-icon">
              <i className="fa-solid fa-clipboard-list" aria-hidden="true"></i>
            </div>
            <div className="tdv-header-text">
              <span className="tdv-task-number">Ticket #{viewTask?.id || "-"}</span>
              <Modal.Title className="tdv-title">{taskDisplayTitle(viewTask) || "-"}</Modal.Title>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {!viewTask ? (
            <div className="unit-loader">
              <Spinner animation="border" />
              <p>Loading ticket...</p>
            </div>
          ) : (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="unit-stat-card tdv-stat-card h-100">
                    <span>Status</span>
                    <strong style={{ fontSize: "16px" }}>
                      <StatusBadge value={viewTask.status} />
                    </strong>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="unit-stat-card tdv-stat-card h-100">
                    <span>Priority</span>
                    <strong>{viewTask.priority || "-"}</strong>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="unit-stat-card tdv-stat-card h-100">
                    <span>Due Date</span>
                    <strong style={{ fontSize: "14px" }}>
                      {fmtDateTime(viewTask.due_at)}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="tdv-info-grid">
                <div className="tdv-info-row">
                  <span className="tdv-info-label">Unit</span>
                  <span className="tdv-info-value">{viewTask.unit?.name || "-"}</span>
                </div>

                <div className="tdv-info-row">
                  <span className="tdv-info-label">Category</span>
                  <span className="tdv-info-value">
                    {viewTask.category?.name || "-"} / {viewTask.sub_category?.name || "-"}
                  </span>
                </div>

                <div className="tdv-info-row">
                  <span className="tdv-info-label">Created By</span>
                  <span className="tdv-info-value">
                    {viewTask.created_by?.name || viewTask.created_by_name || "-"}
                  </span>
                </div>

                <div className="tdv-info-row">
                  <span className="tdv-info-label">Requested For</span>
                  <span className="tdv-info-value">
                    {viewTask.requested_for?.name || viewTask.requested_for_name || "—"}
                  </span>
                </div>

                <div className="tdv-info-row">
                  <span className="tdv-info-label">Assigned To</span>
                  <span className="tdv-info-value">
                    {viewTask.assigned_to?.name || viewTask.assigned_to_name || "Not assigned"}
                  </span>
                </div>
              </div>

              <div className="tdv-meta-bar">
                Created: {fmtDateTime(viewTask.created_at)} | Updated:{" "}
                {fmtDateTime(viewTask.updated_at)}
              </div>

              <div className="mb-3 tdv-section">
                <h6 className="tdv-section-heading">Remarks</h6>

                {viewTask.remarks?.length ? (
                  <div className="admin-task-timeline">
                    {viewTask.remarks.map((remark) => (
                      <div key={remark.id} className="admin-task-timeline-item">
                        <Badge className="unit-status">
                          {remark.remark_type || "general"}
                        </Badge>

                        <p>{remark.remark}</p>

                        <small>
                          {remark.created_by?.name || "-"} @{" "}
                          {fmtDateTime(remark.created_at)}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="unit-empty tdv-empty py-3">
                    <i className="fa-regular fa-comment-dots tdv-empty-icon" aria-hidden="true"></i>
                    <h5>No remarks yet</h5>
                    <p>Add the first remark below.</p>
                  </div>
                )}
              </div>

              <div className="border rounded p-3 mb-3">
                <h6 className="mb-3">Add Remark</h6>

                <div className="row g-2">
                  <div className="col-md-3">
                    <Form.Select
                      value={remarkType}
                      onChange={(e) => setRemarkType(e.target.value)}
                    >
                      <option value="general">General</option>
                      <option value="progress">Progress</option>
                      <option value="issue">Issue</option>
                      <option value="resolution">Resolution</option>
                    </Form.Select>
                  </div>

                  <div className="col-md-7">
                    <Form.Control
                      value={remarkText}
                      onChange={(e) => setRemarkText(e.target.value)}
                      placeholder="Write an update..."
                    />
                  </div>

                  <div className="col-md-2">
                    <Button
                      className="unit-btn unit-btn-primary w-100"
                      onClick={addRemark}
                      disabled={saving || !remarkText.trim()}
                    >
                      <i className="fa-solid fa-plus" aria-hidden="true"></i>
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <h6>Status Timeline</h6>

                {viewTask.status_history?.length ? (
                  <div className="admin-task-timeline">
                    {viewTask.status_history.map((history, index) => (
                      <div key={index} className="admin-task-timeline-item">
                        <p>
                          {history.from_status || "—"} →{" "}
                          <strong>{history.to_status}</strong>
                        </p>

                        <small>
                          by {history.changed_by?.name || "-"} @{" "}
                          {fmtDateTime(history.changed_at)}
                        </small>

                        {history.note ? (
                          <p className="mb-0">({history.note})</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="unit-empty py-3">
                    <h5>No history found</h5>
                    <p>Status history will appear here.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="unit-btn unit-btn-light tdv-close-btn"
            onClick={() => setShowView(false)}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .techtasks-theme .user-select-none {
          user-select: none;
        }

        .techtasks-theme .admin-task-staff-cell strong {
          display: block;
          color: #1f2937;
          font-size: 13px;
          font-weight: 800;
        }

        .techtasks-theme .admin-task-timeline {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .techtasks-theme .admin-task-timeline-item {
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 14px;
          padding: 12px;
        }

        .techtasks-theme .admin-task-timeline-item p {
          margin: 6px 0;
          color: #111827;
        }

        .techtasks-theme .admin-task-timeline-item small {
          color: #6b7280;
        }

        .techtasks-theme .task-action-line {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
        }

        .techtasks-theme .unit-chat {
          background: #ecfdf5 !important;
          color: #047857 !important;
          border-color: #bbf7d0 !important;
        }

        .techtasks-theme .unit-chat:hover {
          background: #047857 !important;
          color: #ffffff !important;
          border-color: #047857 !important;
        }

        .task-chat-overlay {
          position: fixed;
          inset: 0;
          background: rgba(7, 29, 24, 0.34);
          backdrop-filter: blur(3px);
          z-index: 9999;
          display: flex;
          justify-content: flex-end;
        }

        .task-chat-panel {
          width: min(520px, 100%);
          height: 100vh;
          background: #ffffff;
          box-shadow: -18px 0 45px rgba(15, 35, 31, 0.2);
          display: flex;
          flex-direction: column;
          animation: taskChatIn 0.22s ease;
        }

        @keyframes taskChatIn {
          from {
            transform: translateX(45px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .task-chat-tabs {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 12px;
          background: #f8faf9;
          border-bottom: 1px solid #e5e7eb;
        }

        .task-chat-tab {
          border: 1px solid #dbe7e2;
          background: #ffffff;
          color: #52635f;
          border-radius: 999px;
          padding: 7px 10px;
          font-size: 12px;
          font-weight: 800;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.16s ease;
        }

        .task-chat-tab.active {
          background: var(--primary, #6d1530);
          color: #ffffff;
          border-color: var(--primary, #6d1530);
          box-shadow: 0 10px 22px rgba(109, 21, 48, 0.22);
        }

        .task-chat-tab-title {
          display: inline-block;
        }

        .task-chat-tab-close {
          width: 17px;
          height: 17px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          font-size: 14px;
          line-height: 1;
          opacity: 0.78;
        }

        .task-chat-tab-close:hover {
          background: rgba(0, 0, 0, 0.12);
        }

        .task-chat-header {
          padding: 16px 18px;
          border-bottom: 1px solid #eef2f0;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .task-chat-title-wrap {
          min-width: 0;
        }

        .task-chat-title-wrap h5 {
          margin: 0;
          color: #071d18;
          font-size: 18px;
          font-weight: 900;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .task-chat-title-wrap p {
          margin: 6px 0 0;
          color: #6b7d78;
          font-size: 12px;
          font-weight: 700;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }

        .task-chat-main-close {
          width: 34px;
          height: 34px;
          border: none;
          border-radius: 12px;
          background: #f1f5f9;
          color: #334155;
          font-size: 22px;
          line-height: 1;
          cursor: pointer;
        }

        .task-chat-main-close:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        .task-chat-meta {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 12px 18px;
          background: #fbfdfc;
          border-bottom: 1px solid #eef2f0;
        }

        .task-chat-meta div {
          min-width: 0;
          border: 1px solid #eef2f0;
          border-radius: 14px;
          padding: 9px 10px;
          background: #ffffff;
        }

        .task-chat-meta span {
          display: block;
          color: #7a8b87;
          font-size: 10px;
          font-weight: 900;
          text-transform: uppercase;
        }

        .task-chat-meta strong {
          display: block;
          margin-top: 4px;
          color: #071d18;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .task-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 18px;
          background:
            radial-gradient(circle at top left, rgba(109, 21, 48, 0.08), transparent 34%),
            linear-gradient(180deg, #ffffff 0%, #f8faf9 100%);
        }

        .task-chat-empty {
          min-height: 280px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #8a9a96;
        }

        .task-chat-empty div {
          font-size: 36px;
          margin-bottom: 8px;
        }

        .task-chat-empty h6 {
          margin: 0;
          color: #334155;
          font-weight: 900;
        }

        .task-chat-empty p {
          margin: 5px 0 0;
          font-size: 12px;
        }

        .task-chat-msg {
          margin-bottom: 12px;
          max-width: 78%;
          display: flex;
          flex-direction: column;
        }

        .task-chat-msg.mine {
          margin-left: auto;
          align-items: flex-end;
        }

        .task-chat-msg.theirs {
          margin-right: auto;
          align-items: flex-start;
        }

        .task-chat-sender {
          font-size: 11px;
          font-weight: 900;
          color: #52635f;
          margin: 0 0 4px 7px;
        }

        .task-chat-bubble {
          padding: 11px 13px;
          border-radius: 16px;
          font-size: 13px;
          line-height: 1.42;
          white-space: pre-wrap;
          word-break: break-word;
          box-shadow: 0 8px 22px rgba(15, 35, 31, 0.08);
        }

        .task-chat-msg.mine .task-chat-bubble {
          background: var(--primary, #6d1530);
          color: #ffffff;
          border-bottom-right-radius: 5px;
        }

        .task-chat-msg.theirs .task-chat-bubble {
          background: #ffffff;
          color: #071d18;
          border: 1px solid #e5e7eb;
          border-bottom-left-radius: 5px;
        }

        .task-chat-time {
          margin-top: 4px;
          font-size: 10px;
          color: #94a3b8;
          font-weight: 700;
        }

        .task-chat-input-area {
          padding: 12px;
          border-top: 1px solid #e5e7eb;
          background: #ffffff;
          display: flex;
          align-items: flex-end;
          gap: 10px;
        }

        .task-chat-input {
          flex: 1;
          resize: none;
          min-height: 44px;
          max-height: 110px;
          border: 1px solid #dbe7e2;
          border-radius: 16px;
          padding: 11px 13px;
          outline: none;
          color: #071d18;
          font-size: 13px;
          font-weight: 600;
        }

        .task-chat-input:focus {
          border-color: var(--primary, #6d1530);
          box-shadow: 0 0 0 3px rgba(109, 21, 48, 0.12);
        }

        .task-chat-send {
          width: 44px;
          height: 44px;
          border-radius: 16px !important;
          background: var(--primary, #6d1530) !important;
          border-color: var(--primary, #6d1530) !important;
          font-weight: 900 !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }

        .task-chat-send:disabled {
          opacity: 0.55;
        }

        @media (max-width: 768px) {
          .task-chat-panel {
            width: 100%;
          }

          .task-chat-meta {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}